import React, { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { OnboardingFormStepLayout } from '@features/onboarding/layouts/OnboardingFormStepLayout';
import { Form } from '@components/ui/form';
import { FormCard } from '@features/onboarding/components/shared/form-card/FormCard';
import {
  MedicalPlansControls,
  ViewMode,
} from '@features/onboarding/components/shared/form-plan/MedicalPlansControls';
import {
  medicalPlansSchema,
  MedicalPlansSchema,
  // OnboardingSchema,
} from '@features/onboarding/schema/onboarding';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { FormStepProps } from '@features/onboarding/interfaces/FormStepProps.interface';
import { ComparePlansModal } from '@features/onboarding/components/shared/plan-compare/ComparePlan';
import { FormPlanList } from '@features/onboarding/components/shared/form-plan/FormPlanList';
import { CoverageTags } from '@features/onboarding/components/shared/coverage-tags/CoverageTags';
import { useInsuranceStore } from 'src/store/insurance';
import { useApplicationStore } from 'src/store/application';
import { useCompareStore } from 'src/store/compare';
import { usePlan } from '@features/onboarding/hooks/usePlan';
import { sortPlans } from '@utils/sortPlans';
import { useGeneralStore } from 'src/store';
import { useAIStore } from 'src/store/ai';

export const MedicalPlansForm = ({
  onSubmit,
  onInvalid,
  onChange,
  // externalErrorMessage,
}: FormStepProps) => {
  const { siteConfig } = useGeneralStore();
  const sitePlans = siteConfig?.plans;
  const { calculateCoverageLevel, getPlanPrice } = usePlan();
  const data = useOnboardingStore((state) => state.data);
  const applicants = data?.applicants ?? [];
  const extraDependents = applicants.filter(
    (dep) => dep.applicant !== 'primary'
  );
  const showMedicalTags = extraDependents.length > 0;

  const form = useForm<MedicalPlansSchema>({
    resolver: zodResolver(medicalPlansSchema),
    defaultValues: {
      ...data,
    },
  });

  const [viewMode, setViewMode] = React.useState<ViewMode>('list');
  const [sortKey, setSortKey] = React.useState<string>('averagePrice');
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('asc');
  const [expandedPlanIds, setExpandedPlanIds] = React.useState<string[]>([]);

  const {
    selectedPlans,
    removePlan,
    compareViewCategory,
    setCompareViewCategory,
    clearSelectedPlans,
  } = useCompareStore();
  const { plans } = useInsuranceStore();
  const { fetchActiveSubscriptionPlans, isMainPlanRejected, activeCategories, activeSubscriptionPlans } =
    useApplicationStore();
  const { setChangeAfterEstimation, estimationResult } = useAIStore();
  const { acceptedTier, updateTierFromApplicationState } = useApplicationStore();

  useEffect(() => {
    fetchActiveSubscriptionPlans();
  }, [fetchActiveSubscriptionPlans]);

  const getFormCardContent = () => {
    if (isMainPlanRejected) {
      const hasActiveHealthPlans = activeCategories.includes('Major Medical');

      if (hasActiveHealthPlans) {
        return {
          title: '',
          description:
            `**Important Note:** You're welcome to look through the available health plan options, but since you're already covered under a current plan, you won't be able to make a new selection right now. If your situation changes or you become eligible for a new enrollment period, you'll have the chance to update your coverage.\n\nIn the meantime, if you have any questions (or just want to talk things through), our customer service team is always happy to help. You can reach us anytime at **${siteConfig?.emails?.primary || ''}**.`,
        };
      } else {
        return {
          title: '',
          description:
            `**Important Note:** Right now, you can still browse the health plan options, but you won't be able to make a new selection since your last application wasn't approved. No worries—you're welcome to reapply in 90 days if anything about your health.\n\nIn the meantime, if you have questions (or just want to talk things through), our customer service team is always happy to help. You can reach us anytime at **${siteConfig?.emails?.primary || ''}**.`,
        };
      }
    }

    return {
      title: "Let's explore your health plan options.",
      description:
        'Remember, if you have any questions, want to compare them, or even see real-world scenarios, just ask Alia. She really knows her stuff!',
    };
  };

  const formCardContent = getFormCardContent();

  const DEFAULT_IMAGE = '/images/cigna-logo.png';
  
  const medicalTags = useWatch({
    control: form.control,
    name: 'medicalTags',
    defaultValue: [],
  }) || [];

  const selectedMedicalPlan = useWatch({
    control: form.control,
    name: 'medicalPlan',
  });

  const activePlanIds = React.useMemo(
    () => new Set(activeSubscriptionPlans?.map((plan) => plan.planId) || []),
    [activeSubscriptionPlans]
  );

  const availableMedicalPlans = React.useMemo(() => {
    const medical = plans.filter((plan) => plan.category === 'Major Medical');
    if (!sitePlans || sitePlans.length === 0) {
      return medical;
    }
    const allowedIds = new Set(sitePlans);
    return medical.filter(
      (plan) => allowedIds.has(plan.id) || activePlanIds.has(plan.id)
    );
  }, [plans, sitePlans, activePlanIds]);

  const medicalPlans = React.useMemo(() => {
    const coverageLevel = calculateCoverageLevel(medicalTags);

    console.log('acceptedTier', acceptedTier);

    const transformed = availableMedicalPlans
    .map((plan) => {
      const coveragePrice = getPlanPrice(plan, coverageLevel, acceptedTier?.toString() || undefined);
      return {
        _id: plan.id,
        id: plan.uid,
        title: plan.name || plan.marketingName || 'Untitled',
        description: plan.shortDescription || plan.shortDescription || '',
        content: plan.longDescription || plan.shortDescription || '',
        image: DEFAULT_IMAGE,
        price: `$${coveragePrice.toFixed(2)}`,
        prices: plan.prices,
        period: 'per month',
      };
    })

    console.log('transformed', transformed);

    return transformed;
  }, [availableMedicalPlans, medicalTags, acceptedTier]);

  const sortedPlans = React.useMemo(() => {
    return sortPlans(medicalPlans, availableMedicalPlans, sortKey, sortDirection);
  }, [sortKey, sortDirection, medicalPlans, availableMedicalPlans]);

  const comparePlans = medicalPlans.filter((plan) =>
    selectedPlans.includes(plan.id)
  );

  const handleRemovePlan = (id: string) => {
    removePlan(id);
  };

  const handleAddPlan = () => {
    setCompareViewCategory(null);
  };

  const handleCloseCompare = () => {
    setCompareViewCategory(null);
    if (clearSelectedPlans) {
      clearSelectedPlans();
    }
  };

  const handleCompareClick = () => {
    setCompareViewCategory('Major Medical');
  };
  const [lastExpandedPlanId, setLastExpandedPlanId] = React.useState<
    string | null
  >(null);

  React.useEffect(() => {
    setExpandedPlanIds((prev) => {
      const validIds = prev.filter((id) =>
        sortedPlans.some((p) => p.id === id)
      );

      const isLastExpandedValid =
        lastExpandedPlanId &&
        sortedPlans.some((p) => p.id === lastExpandedPlanId);

      if (viewMode === 'grid') {
        if (isLastExpandedValid) {
          if (validIds.includes(lastExpandedPlanId)) {
            return validIds.slice(0, 2);
          } else {
            if (validIds.length < 2) {
              return [...validIds, lastExpandedPlanId];
            } else {
              return [validIds[1], lastExpandedPlanId];
            }
          }
        } else {
          return validIds.slice(0, 2);
        }
      } else {
        if (
          isLastExpandedValid &&
          !validIds.includes(lastExpandedPlanId) &&
          validIds.length < 2
        ) {
          return [...validIds, lastExpandedPlanId];
        }
        return validIds.slice(0, 2);
      }
    });
    updateTierFromApplicationState();
  }, [viewMode, sortedPlans, lastExpandedPlanId]);

  useEffect(() => {
    if (!selectedMedicalPlan) return;

    const isValidSelectedPlan = availableMedicalPlans.some(
      (plan) => plan.uid === selectedMedicalPlan
    );
    if (!isValidSelectedPlan) {
      form.setValue('medicalPlan', null);
      setExpandedPlanIds([]);
      setLastExpandedPlanId(null);
    }
  }, [availableMedicalPlans, selectedMedicalPlan]);

  const toggleExpandAll = (planId: string) => {
    setExpandedPlanIds((prev) => {
      const next = prev.includes(planId)
        ? prev.filter((id) => id !== planId)
        : [...prev, planId];
      return next;
    });
  };

  useEffect(() => {
    const coverageLevel = calculateCoverageLevel(medicalTags);

    useOnboardingStore.setState((state) => ({
      data: {
        ...state.data,
        medicalTags: medicalTags,
      },
    }));

    const selectedPlanUid = form.getValues('medicalPlan');
    if (!selectedPlanUid) return;

    const selectedPlan = availableMedicalPlans.find((p) => p.uid === selectedPlanUid);
    if (!selectedPlan) return;

    const price =
      selectedPlan.prices?.find((p) => p.coverage === coverageLevel)?.price ??
      0;

    useOnboardingStore.setState((state) => ({
      data: {
        ...state.data,
        medicalPlanPrice: price,
        medicalTags: medicalTags,
      },
    }));
  }, [medicalTags, availableMedicalPlans]);

  // Track changes to Major Medical plan after estimation
  useEffect(() => {
    // Only track changes if estimation has been run and a plan is selected
    if (estimationResult && selectedMedicalPlan) {
      setChangeAfterEstimation(true);
    }
  }, [selectedMedicalPlan, estimationResult, setChangeAfterEstimation]);

  return (
    <OnboardingFormStepLayout
      isFormValid={form.formState.isValid}
      handleFormSubmit={form.handleSubmit(
        (data) => {
          console.log('[MedicalPlansForm] onSubmit called with:', data);
          return onSubmit(data);
        },
        (errors) => {
          console.log('[MedicalPlansForm] onInvalid called with:', errors);
          return onInvalid(errors);
        }
      )}
      onChange={form.handleSubmit((data) => {
        console.log('[MedicalPlansForm] onChange called with:', data);
        return onChange(data);
      })}
    >
      {compareViewCategory ? (
        <ComparePlansModal
          plans={comparePlans}
          onRemovePlan={handleRemovePlan}
          onAddPlan={handleAddPlan}
          onReplacePlan={(oldId, newId) => {
            removePlan(oldId);
            const { togglePlan } = useCompareStore.getState();
            togglePlan(newId);
          }}
          onClose={handleCloseCompare}
          allPlans={availableMedicalPlans}
        />
      ) : (
        <Form {...form}>
          <form className="mt-0.5 md:m-0 flex flex-col gap-6">
            <FormCard
              index={2}
              imageSrc="/images/select-plan.svg"
              title={formCardContent.title}
              description={formCardContent.description}
            >
              <CoverageTags
                form={form}
                extraDependents={extraDependents}
                planType="medical"
                showTags={showMedicalTags}
              />
              <MedicalPlansControls
                form={form}
                viewMode={viewMode}
                setViewMode={setViewMode}
                onSortChange={(key, direction) => {
                  setSortKey(key);
                  setSortDirection(direction);
                }}
                compareName=""
                plans={medicalPlans}
                onOpenCompare={handleCompareClick}
              />

              <FormPlanList
                plans={sortedPlans}
                form={form}
                name="medicalPlan"
                viewMode={viewMode}
                hideRadio={isMainPlanRejected}
                expandedPlanIds={expandedPlanIds}
                onToggleExpand={(planId) => toggleExpandAll(planId)}
                planLength={sortedPlans.length}
                planType={'mental'}
              />
            </FormCard>
          </form>
        </Form>
      )}
    </OnboardingFormStepLayout>
  );
};
